import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Target, Award, Users, TrendingUp } from 'lucide-react';
import { mockCompanyInfo } from '../mock';

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const features = [
    {
      icon: Target,
      title: 'Precision & Accuracy',
      description: 'Every task executed with meticulous attention to detail'
    },
    {
      icon: Award,
      title: 'Industry Excellence',
      description: 'Award-winning service across all sectors'
    },
    {
      icon: Users,
      title: 'Dedicated Team',
      description: 'Expert professionals committed to your success'
    },
    {
      icon: TrendingUp,
      title: 'Proven Results',
      description: 'Consistent delivery that drives business growth'
    }
  ];

  return (
    <section id="about" ref={ref} className="relative py-32 bg-white overflow-hidden">
      {/* Decorative Elements */}

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <div className="inline-block px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm font-semibold mb-6">
            ABOUT US
          </div>
          <h2 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Who We Are
          </h2>
        </motion.div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Left: Description */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="space-y-6">
              <p className="text-2xl text-slate-800 font-light leading-relaxed">
                {mockCompanyInfo.description}
              </p>
              <div className="pt-6 border-l-4 border-yellow-500 pl-6">
                <p className="text-lg text-slate-700 italic">
                  &ldquo;{mockCompanyInfo.mission}&rdquo;
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right: Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-square">
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=900&q=80)' }}
              />
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-transparent" />
            </div>
            {/* Floating Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="absolute -bottom-8 -left-8 bg-white p-8 rounded-2xl shadow-xl border border-slate-200"
            >
              <div className="text-5xl font-bold text-yellow-600 mb-2">13+</div>
              <div className="text-slate-700 font-medium">Years of Excellence</div>
            </motion.div>
          </motion.div>
        </div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                className="group p-8 bg-slate-50 hover:bg-blue-50 rounded-2xl transition-all duration-300 hover:shadow-lg hover:-translate-y-2"
              >
                <div className="mb-6 inline-block p-4 bg-white group-hover:bg-blue-600 rounded-xl transition-colors duration-300">
                  <Icon className="w-8 h-8 text-blue-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default About;
